import React, { useState, useEffect } from 'react';
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CButton,
  CAlert,
  CRow,
  CCol,
  CInputGroup,
  CInputGroupText,
  CCard,        
  CCardBody 
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilBuilding, cilEnvelopeClosed, cilPhone, cilUser, cilContact } from '@coreui/icons';
import { superAdminAPI } from '../services/api';

const ClientModal = ({ 
  show, 
  onClose, 
  onSave, 
  client = null,
  mode = 'create' // 'create' or 'edit'
}) => {
  const [formData, setFormData] = useState({
    name: '',
    resellerid: '',
    resellername: '',
    custmertype: 'Reseller',
    reseller_when_sold_by_distributor: '',
    client_owner_email: '',
    client_owner_email_2: '',
    reseller_contact_full_name: '',
    reseller_contact_email: '',
    'billiing_street_(end_user)': '',
    billing_city: '',
    billingzipcode: '',
    'billing_state/provience': '',
    billing_country: 'US',
    billing_phone: ''
  });
  
  const [resellers, setResellers] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [loadingResellers, setLoadingResellers] = useState(false);
  const [selectedResellerDetails, setSelectedResellerDetails] = useState(null);

  // Fetch resellers for dropdown
  useEffect(() => {
    const fetchResellers = async () => {
      setLoadingResellers(true);
      try {
        const response = await superAdminAPI.getResellersForDropdown();
        if (response.data.success) {
          setResellers(response.data.resellers);
        }
      } catch (error) {
        console.error('Failed to fetch resellers:', error);
      } finally {
        setLoadingResellers(false);
      }
    };
    
    if (show) {
      fetchResellers();
    }
  }, [show]);

  // Prefill form when editing
  useEffect(() => {
    if (client && mode === 'edit') {
      setFormData({
        name: client.name || '',
        resellerid: client.resellerid || '',
        resellername: client.resellername || '',
        custmertype: client.custmertype || 'Reseller',
        reseller_when_sold_by_distributor: client.reseller_when_sold_by_distributor || '',
        client_owner_email: client.client_owner_email || '',
        client_owner_email_2: client.client_owner_email_2 || '',
        reseller_contact_full_name: client.reseller_contact_full_name || '',
        reseller_contact_email: client.reseller_contact_email || '',
        'billiing_street_(end_user)': client['billiing_street_(end_user)'] || '',
        billing_city: client.billing_city || '',
        billingzipcode: client.billingzipcode || '',
        'billing_state/provience': client['billing_state/provience'] || '',
        billing_country: client.billing_country || 'US',
        billing_phone: client.billing_phone || ''
      });
      
      // If resellerid exists, find and set reseller details
      if (client.resellerid && resellers.length > 0) {
        const reseller = resellers.find(r => r.resellerid === client.resellerid);
        if (reseller) {
          setSelectedResellerDetails({
            company_name: reseller.company_name,
            admin_contact: reseller.admin_contact,
            admin_login_email: reseller.admin_login_email,
            phone: reseller.phone,
            location: reseller.location
          });
        }
      }
    } else {
      // Reset form for create mode
      setFormData({
        name: '',
        resellerid: '',
        resellername: '',
        custmertype: 'Reseller',
        reseller_when_sold_by_distributor: '',
        client_owner_email: '',
        client_owner_email_2: '',
        reseller_contact_full_name: '',
        reseller_contact_email: '',
        'billiing_street_(end_user)': '',
        billing_city: '',
        billingzipcode: '',
        'billing_state/provience': '',
        billing_country: 'US',
        billing_phone: ''
      });
      setSelectedResellerDetails(null);
    }
    setErrors({});
    setServerError('');
  }, [client, mode, show, resellers]);

  // Handle reseller selection
  const handleResellerChange = async (e) => {
    const resellerId = e.target.value;
    
    if (!resellerId) {
      setFormData(prev => ({
        ...prev,
        resellerid: '',
        resellername: '',
        reseller_contact_full_name: '',
        reseller_contact_email: ''
      }));
      setSelectedResellerDetails(null);
      return;
    }
    
    // Find reseller in local cache first
    const selectedReseller = resellers.find(r => r.resellerid === resellerId);
    
    if (selectedReseller) {
      // Update form with reseller details
      setFormData(prev => ({
        ...prev,
        resellerid: resellerId,
        resellername: selectedReseller.company_name || '',
        reseller_contact_full_name: selectedReseller.admin_contact || '',
        reseller_contact_email: selectedReseller.admin_login_email || ''
      }));
      
      // Set reseller details for display
      setSelectedResellerDetails({
        company_name: selectedReseller.company_name,
        admin_contact: selectedReseller.admin_contact,
        admin_login_email: selectedReseller.admin_login_email,
        phone: selectedReseller.phone,
        location: selectedReseller.location,
        margin: selectedReseller.margin,
        payment_mode: selectedReseller.payment_mode
      });
    } else {
      // If reseller not found in cache, fetch from API
      try {
        const response = await superAdminAPI.getSingleReseller(resellerId);
        if (response.data.success && response.data.reseller) {
          const reseller = response.data.reseller;
          setFormData(prev => ({
            ...prev,
            resellerid: resellerId,
            resellername: reseller.company_name || '',
            reseller_contact_full_name: reseller.admin_contact || '',
            reseller_contact_email: reseller.admin_login_email || ''
          }));
          
          setSelectedResellerDetails({
            company_name: reseller.company_name,
            admin_contact: reseller.admin_contact,
            admin_login_email: reseller.admin_login_email,
            phone: reseller.phone,
            location: reseller.location,
            margin: reseller.margin,
            payment_mode: reseller.payment_mode
          });
        }
      } catch (error) {
        console.error('Failed to fetch reseller details:', error);
      }
    }
    
    // Clear any existing errors
    if (errors.resellerid) {
      setErrors(prev => ({ ...prev, resellerid: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    const requiredFields = [
      'name', 'resellerid', 'resellername', 'custmertype',
      'client_owner_email', 'billiing_street_(end_user)',
      'billing_city', 'billingzipcode', 'billing_state/provience',
      'billing_country', 'billing_phone'
    ];
    
    requiredFields.forEach(field => {
      if (!formData[field]?.toString().trim()) {
        const fieldName = field.replace(/_/g, ' ').replace('(end user)', '').trim();
        newErrors[field] = `${fieldName} is required`;
      }
    });
    
    // Email validation
    if (formData.client_owner_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.client_owner_email)) {
      newErrors.client_owner_email = 'Invalid email format';
    }
    
    if (formData.client_owner_email_2 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.client_owner_email_2)) {
      newErrors.client_owner_email_2 = 'Invalid email format';
    }
    
    if (formData.reseller_contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.reseller_contact_email)) {
      newErrors.reseller_contact_email = 'Invalid email format';
    }
    
    // Phone validation
    if (formData.billing_phone && !/^[\d\s\-\+\(\)\.]+$/.test(formData.billing_phone)) {
      newErrors.billing_phone = 'Invalid phone number';
    }
    
    // Zip code validation (US format)
    if (formData.billing_country === 'US' && formData.billingzipcode) {
      const zipRegex = /^\d{5}(-\d{4})?$/;
      if (!zipRegex.test(formData.billingzipcode)) {
        newErrors.billingzipcode = 'Invalid US zip code format (e.g., 12345 or 12345-6789)';
      }
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    setLoading(true);
    setServerError('');
    
    try {
      // Prepare data for API
      const submitData = {
        name: formData.name.trim(),
        resellerid: formData.resellerid.trim(),
        resellername: formData.resellername.trim(),
        custmertype: formData.custmertype,
        reseller_when_sold_by_distributor: formData.reseller_when_sold_by_distributor.trim(),
        client_owner_email: formData.client_owner_email.toLowerCase().trim(),
        client_owner_email_2: formData.client_owner_email_2.toLowerCase().trim(),
        reseller_contact_full_name: formData.reseller_contact_full_name.trim(),
        reseller_contact_email: formData.reseller_contact_email.toLowerCase().trim(),
        'billiing_street_(end_user)': formData['billiing_street_(end_user)'].trim(),
        billing_city: formData.billing_city.trim(),
        billingzipcode: formData.billingzipcode.trim(),
        'billing_state/provience': formData['billing_state/provience'].trim(),
        billing_country: formData.billing_country.trim(),
        billing_phone: formData.billing_phone.trim()
      };
      
      await onSave(submitData, mode);
      onClose();
    } catch (error) {
      setServerError(error.response?.data?.error || 'Failed to save client');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const countryOptions = [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'UK', label: 'United Kingdom' },
    { value: 'AU', label: 'Australia' },
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'JP', label: 'Japan' },
    { value: 'IN', label: 'India' },
    { value: 'CN', label: 'China' },
    { value: 'BR', label: 'Brazil' },
    { value: 'MX', label: 'Mexico' },
    { value: 'AE', label: 'United Arab Emirates' },
    { value: 'SA', label: 'Saudi Arabia' },
    { value: 'SG', label: 'Singapore' }
  ];

  return (
    <CModal 
      visible={show} 
      onClose={onClose}
      size="xl"
      backdrop="static"
      scrollable
    >
      <CModalHeader>
        <CModalTitle>
          {mode === 'create' ? 'Add New Client' : 'Edit Client'}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        {serverError && (
          <CAlert color="danger" className="mb-3">
            {serverError}
          </CAlert>
        )}
        
        <CForm onSubmit={handleSubmit} id="clientForm">
          {/* Reseller Information Card */}
          <CCard className="mb-4 bg-light">
            <CCardBody>
              <h6 className="mb-3">
                <CIcon icon={cilBuilding} className="me-2" />
                Reseller Information
              </h6>
              
              <CRow className="mb-3">
                <CCol md={6}>
                  <CFormLabel htmlFor="resellerid">Select Reseller *</CFormLabel>
                  <CFormSelect
                    id="resellerid"
                    name="resellerid"
                    value={formData.resellerid}
                    onChange={handleResellerChange}
                    disabled={loadingResellers}
                    invalid={!!errors.resellerid}
                  >
                    <option value="">-- Select a reseller --</option>
                    {loadingResellers ? (
                      <option disabled>Loading resellers...</option>
                    ) : (
                      resellers.map(reseller => (
                        <option key={reseller.resellerid} value={reseller.resellerid}>
                          {reseller.company_name} ({reseller.resellerid})
                        </option>
                      ))
                    )}
                  </CFormSelect>
                  {errors.resellerid && (
                    <div className="invalid-feedback d-block">
                      {errors.resellerid}
                    </div>
                  )}
                </CCol>
                <CCol md={6}>
                  <CFormLabel htmlFor="resellername">Reseller Name *</CFormLabel>
                  <CFormInput
                    id="resellername"
                    name="resellername"
                    value={formData.resellername}
                    onChange={handleChange}
                    placeholder="Will auto-fill when reseller is selected"
                    readOnly
                    invalid={!!errors.resellername}
                  />
                  {errors.resellername && (
                    <div className="invalid-feedback d-block">
                      {errors.resellername}
                    </div>
                  )}
                </CCol>
              </CRow>
              
              {/* Reseller Details Display */}
              {selectedResellerDetails && (
                <div className="mt-3 p-3 bg-white rounded border">
                  <h6 className="text-primary mb-2">
                    <CIcon icon={cilContact} className="me-2" />
                    Selected Reseller Details
                  </h6>
                  <CRow>
                    <CCol md={6}>
                      <div className="mb-2">
                        <small className="text-muted">Company:</small>
                        <div className="fw-semibold">{selectedResellerDetails.company_name}</div>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">Admin Contact:</small>
                        <div className="fw-semibold">{selectedResellerDetails.admin_contact}</div>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">Margin:</small>
                        <div className="fw-semibold">{selectedResellerDetails.margin}%</div>
                      </div>
                    </CCol>
                    <CCol md={6}>
                      <div className="mb-2">
                        <small className="text-muted">Email:</small>
                        <div className="fw-semibold">
                          <a href={`mailto:${selectedResellerDetails.admin_login_email}`} className="text-decoration-none">
                            {selectedResellerDetails.admin_login_email}
                          </a>
                        </div>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">Phone:</small>
                        <div className="fw-semibold">{selectedResellerDetails.phone}</div>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">Payment Mode:</small>
                        <div className="fw-semibold">{selectedResellerDetails.payment_mode}</div>
                      </div>
                    </CCol>
                  </CRow>
                  <div className="mt-2">
                    <small className="text-muted">Location:</small>
                    <div className="fw-semibold small">{selectedResellerDetails.location}</div>
                  </div>
                </div>
              )}
            </CCardBody>
          </CCard>
          
          <h5 className="mb-3">
            <CIcon icon={cilUser} className="me-2" />
            Client Information
          </h5>
          
          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel htmlFor="name">Client Name *</CFormLabel>
              <CFormInput
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter client company name"
                invalid={!!errors.name}
              />
              {errors.name && (
                <div className="invalid-feedback d-block">
                  {errors.name}
                </div>
              )}
            </CCol>
            <CCol md={6}>
              <CFormLabel htmlFor="custmertype">Customer Type *</CFormLabel>
              <CFormSelect
                id="custmertype"
                name="custmertype"
                value={formData.custmertype}
                onChange={handleChange}
                invalid={!!errors.custmertype}
              >
                <option value="Reseller">Reseller</option>
                <option value="Distributor">Distributor</option>
              </CFormSelect>
              {errors.custmertype && (
                <div className="invalid-feedback d-block">
                  {errors.custmertype}
                </div>
              )}
            </CCol>
          </CRow>
          
          {formData.custmertype === 'Distributor' && (
            <CRow className="mb-3">
              <CCol md={12}>
                <CFormLabel htmlFor="reseller_when_sold_by_distributor">
                  Reseller Contact (when sold by distributor)
                </CFormLabel>
                <CFormInput
                  id="reseller_when_sold_by_distributor"
                  name="reseller_when_sold_by_distributor"
                  value={formData.reseller_when_sold_by_distributor}
                  onChange={handleChange}
                  placeholder="Email of reseller contact for distributor sales"
                  type="email"
                  invalid={!!errors.reseller_when_sold_by_distributor}
                />
                {errors.reseller_when_sold_by_distributor && (
                  <div className="invalid-feedback d-block">
                    {errors.reseller_when_sold_by_distributor}
                  </div>
                )}
              </CCol>
            </CRow>
          )}
          
          <h5 className="mb-3 mt-4">
            <CIcon icon={cilUser} className="me-2" />
            Contact Information
          </h5>
          
          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel htmlFor="client_owner_email">Primary Email *</CFormLabel>
              <CFormInput
                type="email"
                id="client_owner_email"
                name="client_owner_email"
                value={formData.client_owner_email}
                onChange={handleChange}
                placeholder="Primary contact email"
                invalid={!!errors.client_owner_email}
              />
              {errors.client_owner_email && (
                <div className="invalid-feedback d-block">
                  {errors.client_owner_email}
                </div>
              )}
            </CCol>
            <CCol md={6}>
              <CFormLabel htmlFor="client_owner_email_2">Secondary Email</CFormLabel>
              <CFormInput
                type="email"
                id="client_owner_email_2"
                name="client_owner_email_2"
                value={formData.client_owner_email_2}
                onChange={handleChange}
                placeholder="Secondary contact email"
                invalid={!!errors.client_owner_email_2}
              />
              {errors.client_owner_email_2 && (
                <div className="invalid-feedback d-block">
                  {errors.client_owner_email_2}
                </div>
              )}
            </CCol>
          </CRow>
          
          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel htmlFor="reseller_contact_full_name">Reseller Contact Name</CFormLabel>
              <CFormInput
                id="reseller_contact_full_name"
                name="reseller_contact_full_name"
                value={formData.reseller_contact_full_name}
                onChange={handleChange}
                placeholder="Full name of reseller contact"
                readOnly
                invalid={!!errors.reseller_contact_full_name}
              />
              <small className="text-muted">Auto-filled from selected reseller</small>
              {errors.reseller_contact_full_name && (
                <div className="invalid-feedback d-block">
                  {errors.reseller_contact_full_name}
                </div>
              )}
            </CCol>
            <CCol md={6}>
              <CFormLabel htmlFor="reseller_contact_email">Reseller Contact Email</CFormLabel>
              <CFormInput
                type="email"
                id="reseller_contact_email"
                name="reseller_contact_email"
                value={formData.reseller_contact_email}
                onChange={handleChange}
                placeholder="Email of reseller contact"
                readOnly
                invalid={!!errors.reseller_contact_email}
              />
              <small className="text-muted">Auto-filled from selected reseller</small>
              {errors.reseller_contact_email && (
                <div className="invalid-feedback d-block">
                  {errors.reseller_contact_email}
                </div>
              )}
            </CCol>
          </CRow>
          
          <h5 className="mb-3 mt-4">
            <CIcon icon={cilBuilding} className="me-2" />
            Client Billing Address
          </h5>
          
          <CRow className="mb-3">
            <CCol md={12}>
              <CFormLabel htmlFor="billiing_street_(end_user)">Street Address *</CFormLabel>
              <CFormTextarea
                id="billiing_street_(end_user)"
                name="billiing_street_(end_user)"
                value={formData['billiing_street_(end_user)']}
                onChange={handleChange}
                placeholder="Enter street address"
                rows="2"
                invalid={!!errors['billiing_street_(end_user)']}
              />
              {errors['billiing_street_(end_user)'] && (
                <div className="invalid-feedback d-block">
                  {errors['billiing_street_(end_user)']}
                </div>
              )}
            </CCol>
          </CRow>
          
          <CRow className="mb-3">
            <CCol md={4}>
              <CFormLabel htmlFor="billing_city">City *</CFormLabel>
              <CFormInput
                id="billing_city"
                name="billing_city"
                value={formData.billing_city}
                onChange={handleChange}
                placeholder="City"
                invalid={!!errors.billing_city}
              />
              {errors.billing_city && (
                <div className="invalid-feedback d-block">
                  {errors.billing_city}
                </div>
              )}
            </CCol>
            <CCol md={4}>
              <CFormLabel htmlFor="billing_state/provience">State/Province *</CFormLabel>
              <CFormInput
                id="billing_state/provience"
                name="billing_state/provience"
                value={formData['billing_state/provience']}
                onChange={handleChange}
                placeholder="State/Province"
                invalid={!!errors['billing_state/provience']}
              />
              {errors['billing_state/provience'] && (
                <div className="invalid-feedback d-block">
                  {errors['billing_state/provience']}
                </div>
              )}
            </CCol>
            <CCol md={4}>
              <CFormLabel htmlFor="billingzipcode">Zip/Postal Code *</CFormLabel>
              <CFormInput
                id="billingzipcode"
                name="billingzipcode"
                value={formData.billingzipcode}
                onChange={handleChange}
                placeholder="Zip/Postal Code"
                invalid={!!errors.billingzipcode}
              />
              {errors.billingzipcode && (
                <div className="invalid-feedback d-block">
                  {errors.billingzipcode}
                </div>
              )}
            </CCol>
          </CRow>
          
          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel htmlFor="billing_country">Country *</CFormLabel>
              <CFormSelect
                id="billing_country"
                name="billing_country"
                value={formData.billing_country}
                onChange={handleChange}
                invalid={!!errors.billing_country}
              >
                {countryOptions.map(country => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </CFormSelect>
              {errors.billing_country && (
                <div className="invalid-feedback d-block">
                  {errors.billing_country}
                </div>
              )}
            </CCol>
            <CCol md={6}>
              <CFormLabel htmlFor="billing_phone">Phone Number *</CFormLabel>
              <CInputGroup>
                <CInputGroupText>
                  <CIcon icon={cilPhone} />
                </CInputGroupText>
                <CFormInput
                  id="billing_phone"
                  name="billing_phone"
                  value={formData.billing_phone}
                  onChange={handleChange}
                  placeholder="Phone number"
                  invalid={!!errors.billing_phone}
                />
              </CInputGroup>
              {errors.billing_phone && (
                <div className="invalid-feedback d-block">
                  {errors.billing_phone}
                </div>
              )}
            </CCol>
          </CRow>
        </CForm>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </CButton>
        <CButton 
          color="primary" 
          type="submit" 
          form="clientForm"
          disabled={loading}
        >
          {loading ? 'Saving...' : mode === 'create' ? 'Create Client' : 'Update Client'}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default ClientModal;